import React from "react";
import {
  GdprSccVendorReviewStatus,
  OrganizationDataRecipientDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { VendorClassificationDto } from "../../../../common/service/server/dto/VendorDto";
import { VendorRadioOption } from "../../../components/get-compliant/vendors/VendorFlagWithDocumentation";
import { ClassificationType } from "../../../components/organisms/data-recipients/DataRecipientDisclosureSettings";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { VENDOR_CATEGORY_AD_NETWORK } from "../../../types/Vendor";
import { ToolTip } from "@truevault/aquilo";
import { QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { isShopifyAudiences } from "../../../../polaris/util/vendors";

export const getVendorClassificationSlug = (
  vendor: OrganizationDataRecipientDto,
): string | null | undefined => {
  // Note that we first check for `classificationId` even though it's not a property of `OrganizationDataRecipientDto`
  // because to update a vendor's classification, we set it's `classificationId` property on the vendor update dto object,
  // which is then converted to the right classification on the backend - but here we still only have access to the local state,
  // which uses `classificationId`
  if ("classificationId" in vendor) {
    return vendor?.classificationOptions?.find((o) => o.id === (vendor as any).classificationId)
      ?.slug;
  }
  return vendor?.classification?.slug;
};

export const getClassificationIdForClassificationType = (
  classificationType: ClassificationType,
  classificationOptions: VendorClassificationDto[],
): string | null => {
  let option = classificationOptions.find((opt) => opt.slug == classificationType);
  if (!option) {
    return null;
  }
  return option.id;
};

export const internationalDataTransferOptionsForVendor = (
  vendor: OrganizationDataRecipientDto,
  org: OrganizationDto,
  openSafeCountriesDialog: () => void,
): VendorRadioOption<GdprSccVendorReviewStatus, GdprSccVendorReviewStatus>[] => {
  return [
    {
      value: "DPF_LIST",
      type: "normal",
      dynamic: true,
      label: (recipient) => {
        return (
          <div className="ml-3 flex items-center">
            <div className="font-medium text-[14px] text-gray-700 mr-[12px]">
              {recipient.name} is on the DPF list
            </div>
            <ToolTip
              text={
                <>
                  You can search the{" "}
                  <a
                    className="tooltip-link"
                    href="https://www.dataprivacyframework.gov/list"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    DPF participant list
                  </a>{" "}
                  for your vendor.
                </>
              }
              yOffset={-25}
              width="220px"
            >
              <QuestionMarkCircleIcon
                className="w-[16px] h-[16px] text-neutral-600 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open("https://www.dataprivacyframework.gov/list", "_blank");
                }}
              />
            </ToolTip>
          </div>
        );
      },
      key: "DPF_LIST",
    },
    {
      value: "DOCS_PROVIDED",
      type: "file",
      disabled: false,
      label: `SCC found for ${vendor.name}`,
      key: "DOCS_PROVIDED",
      createFileLink: (recipient) =>
        ApiRouteHelpers.storage.vendorAgreements.byType(org.id, recipient.vendorId, "scc"),
    },
    {
      value: "FULFILLMENT_ONLY",
      type: "normal",
      dynamic: true,
      disabled: vendor.category === VENDOR_CATEGORY_AD_NETWORK,
      disabledTooltip: "This option does not apply to this type of vendor.",
      label: (recipient) => {
        return (
          <div className="ml-3 flex items-center">
            <div className="font-medium text-[14px] text-gray-700 mr-[12px]">
              Vendor is used for contract fulfillment only
            </div>
            <ToolTip
              text="When necessary to perform a contract with the consumer, such as fulfilling an order, the data transfer rules do not apply to the vendor receiving the data in a foreign country."
              yOffset={-25}
              width="220px"
            >
              <QuestionMarkCircleIcon className="w-[16px] h-[16px] text-neutral-600" />
            </ToolTip>
            {recipient.isPotentiallyExceptedFromSccs && (
              <ToolTip
                text="Vendors marked with this icon have been flagged by our team as not requiring documentation."
                yOffset={-25}
                width="220px"
              >
                <img
                  className="ml-xs"
                  src="/assets/images/icon-lightbulb.svg"
                  color="primary"
                  width="16"
                  style={{ marginBottom: "1px" }} // hack for better centered vertical alignment in the row
                />
              </ToolTip>
            )}
          </div>
        );
      },
      key: "FULFILLMENT_ONLY",
    },
    {
      value: "SAFE_COUNTRIES_ONLY",
      type: "normal",
      dynamic: true,
      label: (_recipient) => {
        return (
          <div className="ml-3 text-[14px] text-gray-700 font-medium">
            This vendor processes data only in{" "}
            <span
              className="text-weight-medium cursor-pointer text-decoration-underline color-secondary-600"
              onClick={openSafeCountriesDialog}
            >
              safe countries
            </span>
          </div>
        );
      },
      key: "SAFE_COUNTRIES_ONLY",
    },
  ];
};

export const isVendorServiceClassificationLockedToThirdParty = (
  vendor: OrganizationDataRecipientDto,
) => {
  const autoSelectThirdParty = Boolean(
    vendor.ccpaIsSelling ||
      vendor.isAlwaysDataSellingVendor ||
      vendor.ccpaIsSharing ||
      vendor.usesCustomAudience,
  );

  return isShopifyAudiences(vendor) || autoSelectThirdParty;
};
